import { isEmpty } from 'lodash'
import { ReactElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { FormattedPrice } from '@10xLabs/react-web/components/FormattedPrice'
import { FormattedPercent } from '@10xLabs/react-web/components/FormattedPercent'
import { FormattedDateTime } from '@10xLabs/react-web/components/FormattedDateTime'
import { StyledLink } from 'components/v2/Link'
import { Dropdown } from 'components/v2/Dropdown'
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from 'components/v2/Table'
import { CampaignStatus } from 'features/Campaigns/components/Status'
import { channelMessages } from 'features/Coupons/components/Channel/messages'
import { DISCOUNT_TYPE } from 'features/Coupons/components/DiscountType/constants'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { isSuperAdmin, canWrite } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { GTCampaignSegment, GTCoupon } from 'gql/types.generated'
import { getStatus } from 'resolvers/campaigns'
import { CouponsActions } from './Actions'

const DurationWrapper = styled.div<TSFixMe>`
  display: flex;
  flex-wrap: wrap;

  > div {
    margin-left: 2px;
  }
`

const MinSaleWrapper = styled.div<TSFixMe>`
  white-space: nowrap;
`

const getSegmentsValue = (legSegments: GTCampaignSegment[]): ReactElement => {
  if (isEmpty(legSegments)) {
    return <FormattedMessage id="coupons.index.citySegments.all" defaultMessage="All segments" />
  }
  return (
    <Dropdown toggle={<StyledLink>{legSegments?.length}</StyledLink>} noPadding fitContent>
      <Table>
        <Thead>
          <Tr>
            <Th>
              <FormattedMessage id="dictionary.origin" defaultMessage="Origin" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.destination" defaultMessage="Destination" />
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {legSegments.map(({ origin, destination }, index) => {
            return (
              <Tr key={index}>
                <Td>{origin?.name}</Td>
                <Td>{destination?.name}</Td>
              </Tr>
            )
          })}
        </Tbody>
      </Table>
    </Dropdown>
  )
}

export const CouponsList = ({ coupons = [] }: { coupons: GTCoupon[] }): ReactElement => {
  const intl = useIntl()
  const login = useLogin()

  return (
    <TableContainer overflow>
      <Table marginBottom>
        <Thead>
          <Tr>
            {canWrite(FEATURES.CAMPAIGN, login.accessToken.policies) && <Th />}
            <Th>
              <FormattedMessage id="dictionary.status" defaultMessage="Status" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.code" defaultMessage="Code" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.campaign" defaultMessage="Campaign" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.discount" defaultMessage="Discount" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.duration" defaultMessage="Duration" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.usage" defaultMessage="Usage" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.channels" defaultMessage="Channels" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.segments" defaultMessage="Segments" />
            </Th>
            {isSuperAdmin(login.accessToken.conditions) && (
              <Th>
                <FormattedMessage id="dictionary.company" defaultMessage="Company" />
              </Th>
            )}
          </Tr>
        </Thead>
        <Tbody>
          {coupons.map((coupon): ReactElement => {
            const citySegments = coupon.constraints.citySegments || []
            const segments = citySegments.reduce((acc: TSFixMe[], { segments }) => [...acc, ...segments], [])

            return (
              <Tr key={coupon.id}>
                {canWrite(FEATURES.CAMPAIGN, login.accessToken.policies) && (
                  <Td>
                    <CouponsActions coupon={coupon} />
                  </Td>
                )}
                <Td>
                  <CampaignStatus status={getStatus(coupon)} />
                </Td>
                <Td>{coupon.code}</Td>
                <Td>{coupon.campaign.name}</Td>
                <Td>
                  <div>
                    {coupon.discountType === DISCOUNT_TYPE.PERCENTAGE ? (
                      <FormattedPercent value={coupon.value} />
                    ) : (
                      <FormattedPrice value={coupon.value} />
                    )}
                  </div>
                  {(coupon.constraints.minSale || 0) > 0 && (
                    <MinSaleWrapper>
                      min. <FormattedPrice value={coupon.constraints.minSale} />
                    </MinSaleWrapper>
                  )}
                </Td>
                <Td>
                  <DurationWrapper>
                    <span>Starts:</span>
                    <FormattedDateTime date={coupon.startsAt} intl={intl} />
                  </DurationWrapper>
                  <DurationWrapper>
                    <span>Ends:</span>

                    <FormattedDateTime date={coupon.endsAt} intl={intl} />
                  </DurationWrapper>
                </Td>
                <Td>
                  <div>
                    <FormattedMessage
                      id="coupons.index.usage.value"
                      defaultMessage="{usedCount} of {limit}"
                      values={{
                        usedCount: coupon.usedCount,
                        limit: coupon.constraints.limit
                      }}
                    />
                  </div>
                  <div>
                    {coupon.constraints.userLimit === 1 ? (
                      <FormattedMessage id="coupons.index.singleUse" defaultMessage="Single use" />
                    ) : (
                      <FormattedMessage id="coupons.index.multipleUse" defaultMessage="Multiple uses" />
                    )}
                  </div>
                </Td>
                <Td>
                  {coupon.channels
                    .map((channel: TSFixMe): TSFixMe => {
                      return channelMessages[channel] ? intl.formatMessage(channelMessages[channel]) : channel
                    })
                    .join(', ')}
                </Td>
                <Td>
                  <div>{getSegmentsValue(segments)}</div>
                </Td>
                {isSuperAdmin(login.accessToken.conditions) && <Td>{coupon.campaign.company.name}</Td>}
              </Tr>
            )
          })}
        </Tbody>
      </Table>
    </TableContainer>
  )
}
